import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  isSinglePhotoSlot, MAX_INTERIOR_PHOTOS, missingSlots, OPTIONAL_SLOTS, RECORD_SLOTS, SIDE_SLOTS,
  type InspectionSlot,
} from '@adbonus/core';
import { ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { useApiError } from '../../lib/useApiError';
import { photoUrl, shrinkPhoto, type ShrunkPhoto } from '../../lib/photo';
import {
  useDeletePhoto, useInspection, usePatchInspection, useStartInspection, useUploadPhoto,
  type PhotoUpload,
} from '../../lib/queries';
import type { InspectionPhoto } from '../../lib/types';
import { Alert, Field, Label } from '../ui';
import { CameraIcon } from './CameraIcon';
import { VinPanel, type FreshVin, type VinVehicle } from './VinPanel';
import { VinScanner } from './VinScanner';
import type { FoundVin } from '../../lib/vin';
import { DamageForm, type DamageDetails } from './DamageForm';
import { orderPhotos } from './InspectionGallery';
import { PhotoViewer } from './PhotoViewer';
import { useInspectionWords, usePhotoLabel } from './labels';

/** Long enough for the photos of the first side to go first. */
const PRELOAD_AFTER_MS = 3000;

export type CaptureTarget =
  | { kind: 'INTAKE'; vehicleId: string }
  /** An intake taken in with its photos left for later, finished by order. */
  | { kind: 'INTAKE'; orderId: string }
  | { kind: 'HANDOVER'; orderId: string };

export interface CaptureState {
  inspectionId: string | null;
  missing: InspectionSlot[];
  /** A photo is still being shrunk or sent; finishing now would lose it. */
  busy: boolean;
  photoCount: number;
}

interface Pending {
  slot: InspectionSlot;
  previewUrl: string;
  status: 'uploading' | 'failed';
  upload: PhotoUpload & { key: string };
}


/**
 * Walking round a car with the phone.
 *
 * Each tile opens the camera straight away. A photo is shrunk on the phone and
 * sent the moment it is taken, so the person moves on to the next side while
 * the last one uploads; one that fails stays on screen to be tapped again,
 * with the same key, so a retry never stores it twice.
 */
export function InspectionCapture({ target, vehicle, onState }: {
  target: CaptureTarget;
  /** At intake: the car being taken in, so the VIN read off its plate goes on its record. */
  vehicle?: VinVehicle | undefined;
  onState: (state: CaptureState) => void;
}) {
  const { t } = useI18n();
  const words = useInspectionWords();
  const label = usePhotoLabel();
  const errorText = useApiError();

  const start = useStartInspection();
  const [inspectionId, setInspectionId] = useState<string | null>(null);
  const { data: inspection } = useInspection(inspectionId);
  const upload = useUploadPhoto(inspectionId ?? '');
  const remove = useDeletePhoto(inspectionId ?? '');
  const patch = usePatchInspection(inspectionId ?? '');

  const [pending, setPending] = useState<Record<string, Pending>>({});
  const [shrinking, setShrinking] = useState(false);
  const [damageDraft, setDamageDraft] = useState<ShrunkPhoto | null>(null);
  // Held by id, not position: a photo landing mid-view reorders the list underneath.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [odometer, setOdometer] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [freshVin, setFreshVin] = useState<FreshVin | null>(null);
  const [scanning, setScanning] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  // Photos taken earlier — on this phone or another — are picked from the gallery instead.
  const [fromGallery, setFromGallery] = useState(false);
  const shootFor = useRef<InspectionSlot>('FRONT');

  const targetKey = `${target.kind}:${'vehicleId' in target ? target.vehicleId : target.orderId}`;

  // The plate reader is a few megabytes; fetched in the background so it is ready when the VIN is shot.
  useEffect(() => {
    if (target.kind !== 'INTAKE' || !vehicle) return undefined;
    const timer = window.setTimeout(() => {
      void import('../../lib/vin-paddle').then((m) => m.prepareVinReader()).catch(() => undefined);
    }, PRELOAD_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [targetKey]);
  useEffect(() => {
    setInspectionId(null);
    setPending({});
    start.mutate(target, {
      onSuccess: (started) => {
        setInspectionId(started.id);
        setOdometer(started.odometerKm === null ? '' : String(started.odometerKm));
      },
    });
    // Restarts only when the car or order changes, not on every render of the parent.
  }, [targetKey]);

  const photos = useMemo(() => orderPhotos(inspection?.photos ?? []), [inspection]);
  const bySlot = (slot: InspectionSlot) => photos.find((p) => p.slot === slot);
  const damages = photos.filter((p) => p.slot === 'DAMAGE');
  const missing = missingSlots(target.kind, photos.map((p) => p.slot));
  const viewingIndex = photos.findIndex((p) => p.id === viewingId);
  const uploading = Object.values(pending).some((p) => p.status === 'uploading');

  useEffect(() => {
    onState({ inspectionId, missing, busy: shrinking || uploading, photoCount: photos.length });
    // `missing` is derived from `photos`; listing both would only repeat it.
  }, [inspectionId, photos, shrinking, uploading]);

  const send = (pendingKey: string, entry: Pending) => {
    setPending((prev) => ({ ...prev, [pendingKey]: { ...entry, status: 'uploading' } }));
    // Each upload settles on its own promise: callbacks handed to `mutate` fire only
    // for the latest call, so a photo sent while another was in flight would spin forever.
    upload.mutateAsync(entry.upload).then(
      () => setPending(({ [pendingKey]: done, ...rest }) => {
        if (done) URL.revokeObjectURL(done.previewUrl);
        return rest;
      }),
      () => setPending((prev) => ({ ...prev, [pendingKey]: { ...entry, status: 'failed' } })),
    );
  };

  const queue = (slot: InspectionSlot, photo: ShrunkPhoto, details?: DamageDetails) => {
    const key = crypto.randomUUID();
    // A side has one tile, so its pending entry is keyed by the side; damage and interior pile up.
    const pendingKey = isSinglePhotoSlot(slot) ? slot : key;
    send(pendingKey, {
      slot,
      previewUrl: photo.previewUrl,
      status: 'uploading',
      upload: {
        key, slot,
        image: photo.image, thumb: photo.thumb, width: photo.width, height: photo.height,
        ...(details ?? {}),
      },
    });
  };

  const shoot = (slot: InspectionSlot) => {
    shootFor.current = slot;
    setProblem(null);
    // At intake the VIN is scanned live, where the camera allows it; otherwise it is an ordinary photo.
    if (fromGallery) galleryInput.current?.click();
    else if (slot === 'VIN' && vehicle && 'mediaDevices' in navigator) setScanning(true);
    else fileInput.current?.click();
  };

  /** From the gallery the interior can come several at once, up to what is left of its ten. */
  const onPicked = async (files: File[]) => {
    if (galleryInput.current) galleryInput.current.value = '';
    const many = shootFor.current === 'INTERIOR'
      ? files.slice(0, Math.max(0, MAX_INTERIOR_PHOTOS - interiorCount))
      : files.slice(0, 1);
    for (const file of many) await onFile(file);
  };

  const onFile = async (file: File | undefined, scanned?: FoundVin | null) => {
    if (!file) return;
    // Read the side now: another tile may be tapped while this photo is still shrinking.
    const slot = shootFor.current;
    // The VIN is read from the camera's own full-size picture: its characters are small.
    if (slot === 'VIN') setFreshVin({ source: file, key: Date.now(), scanned });
    setShrinking(true);
    try {
      const shrunk = await shrinkPhoto(file);
      if (slot === 'DAMAGE') setDamageDraft(shrunk);
      else queue(slot, shrunk);
    } catch {
      setProblem(t('inspection.failed'));
    } finally {
      setShrinking(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const viewerActions = (photo: InspectionPhoto) => (
    <>
      {isSinglePhotoSlot(photo.slot) && (
        <button
          onClick={() => { setViewingId(null); shoot(photo.slot); }}
          className="min-h-11 flex-1 rounded-xl bg-white/15 px-3 text-sm font-semibold"
        >
          {t('inspection.retake')}
        </button>
      )}
      <button
        onClick={() => remove.mutate(photo.id, { onSuccess: () => setViewingId(null) })}
        className="min-h-11 flex-1 rounded-xl bg-white/10 px-3 text-sm text-white/80"
      >
        {t('inspection.remove')}
      </button>
    </>
  );

  const tile = (slot: InspectionSlot, required: boolean) => {
    const photo = bySlot(slot);
    const waiting = pending[slot];
    const image = waiting?.previewUrl ?? (photo ? photoUrl(photo.id, 'thumb') : null);
    const onTap = waiting?.status === 'failed'
      ? () => send(slot, waiting)
      : photo && !waiting ? () => setViewingId(photo.id) : () => shoot(slot);

    return (
      <button
        key={slot}
        onClick={onTap}
        disabled={!inspectionId || waiting?.status === 'uploading'}
        className={`relative flex aspect-[4/3] w-full flex-col items-center justify-center overflow-hidden rounded-2xl border text-sm transition ${
          image ? 'border-line' : required
            ? 'border-dashed border-brand/50 bg-brand-wash/40 text-brand hover:border-brand'
            : 'border-dashed border-line bg-raised text-ink-dim hover:border-ink-faint'
        }`}
      >
        {image ? (
          <>
            <img src={image} alt={words.slot(slot)} className={`absolute inset-0 h-full w-full object-cover ${waiting ? 'opacity-50' : ''}`} />
            <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/55 px-2 py-1 text-[0.7rem] font-semibold text-white">
              <span className="truncate">{words.slot(slot)}</span>
              {!waiting && <span aria-hidden className="text-positive">✓</span>}
            </span>
            {waiting?.status === 'uploading' && (
              <span className="absolute inset-0 grid place-items-center">
                <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              </span>
            )}
            {waiting?.status === 'failed' && (
              <span className="absolute inset-0 grid place-items-center bg-danger/70 px-2 text-center text-[0.7rem] font-semibold text-white">
                {t('inspection.failed')}
              </span>
            )}
          </>
        ) : (
          <>
            <CameraIcon className="h-7 w-7" />
            <span className="mt-1 font-semibold">{words.slot(slot)}</span>
          </>
        )}
      </button>
    );
  };

  const pendingDamages = Object.entries(pending).filter(([, p]) => p.slot === 'DAMAGE');
  const scanner = scanning && (
    <VinScanner
      onDone={({ photo, vin }) => { setScanning(false); void onFile(photo, vin); }}
      onFallback={() => { setScanning(false); fileInput.current?.click(); }}
      onClose={() => setScanning(false)}
    />
  );
  const interior = photos.filter((p) => p.slot === 'INTERIOR');
  const pendingInterior = Object.entries(pending).filter(([, p]) => p.slot === 'INTERIOR');
  const interiorCount = interior.length + pendingInterior.length;

  /** The interior as a series: every shot so far, then a tile for the next while there is room. */
  const interiorTiles = () => (
    <Fragment key="interior">
      {interior.map((photo) => (
        <button
          key={photo.id}
          onClick={() => setViewingId(photo.id)}
          className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-line"
        >
          <img src={photoUrl(photo.id, 'thumb')} alt={words.slot('INTERIOR')} className="absolute inset-0 h-full w-full object-cover" />
        </button>
      ))}
      {pendingInterior.map(([key, entry]) => (
        <button
          key={key}
          onClick={() => entry.status === 'failed' && send(key, entry)}
          className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-line"
        >
          <img src={entry.previewUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
          <span className="absolute inset-0 grid place-items-center">
            {entry.status === 'failed'
              ? <span className="bg-danger/70 px-2 text-[0.7rem] font-semibold text-white">{t('inspection.failed')}</span>
              : <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
          </span>
        </button>
      ))}
      {interiorCount < MAX_INTERIOR_PHOTOS && (
        <button
          onClick={() => shoot('INTERIOR')}
          disabled={!inspectionId}
          className="flex aspect-[4/3] w-full flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-raised text-sm text-ink-dim transition hover:border-ink-faint"
        >
          <CameraIcon className="h-7 w-7" />
          <span className="mt-1 font-semibold">{words.slot('INTERIOR')}</span>
          {interiorCount > 0 && <span className="tabular text-xs text-ink-faint">{interiorCount}/{MAX_INTERIOR_PHOTOS}</span>}
        </button>
      )}
    </Fragment>
  );

  return (
    <div>
      {start.error instanceof ApiError && (
        <div className="mb-3"><Alert>{errorText(start.error) ?? start.error.message}</Alert></div>
      )}
      {upload.error instanceof ApiError && (
        <div className="mb-3"><Alert>{errorText(upload.error) ?? upload.error.message}</Alert></div>
      )}
      {problem && <div className="mb-3"><Alert>{problem}</Alert></div>}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => { void onFile(event.target.files?.[0]); }}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => { void onPicked(Array.from(event.target.files ?? [])); }}
      />

      <div className="mb-3 grid grid-cols-2 rounded-xl border border-line p-0.5">
        {([false, true] as const).map((gallery) => (
          <button
            key={String(gallery)}
            type="button"
            onClick={() => setFromGallery(gallery)}
            className={`min-h-9 rounded-lg text-sm font-semibold transition ${fromGallery === gallery ? 'bg-raised text-ink' : 'text-ink-dim'}`}
          >
            {gallery ? t('inspection.fromGallery') : t('inspection.fromCamera')}
          </button>
        ))}
      </div>

      <Label>{t('inspection.sides')}</Label>
      <div className="mt-1.5 grid grid-cols-2 gap-2.5">
        {SIDE_SLOTS.map((slot) => tile(slot, true))}
      </div>

      {/* Taken in, the car is put on record: which car it is, and how far it had been driven. */}
      {target.kind === 'INTAKE' && (
        <div className="mt-4">
          <Label>{t('inspection.records')}</Label>
          <div className="mt-1.5 grid grid-cols-2 gap-2.5">
            {RECORD_SLOTS.map((slot) => tile(slot, true))}
          </div>
          {vehicle && (freshVin || bySlot('VIN')) && (
            <VinPanel
              vehicle={vehicle}
              fresh={freshVin}
              stored={bySlot('VIN') ? photoUrl(bySlot('VIN')!.id) : null}
            />
          )}
        </div>
      )}

      <p className={`mt-2 text-xs ${missing.length === 0 ? 'text-positive' : 'text-warning'}`}>
        {missing.length === 0
          ? `✓ ${t('inspection.allRequired')}`
          : t('inspection.missing', { list: missing.map((s) => words.slot(s)).join(', ') })}
      </p>

      <div className="mt-4">
        <Label>{t('inspection.extras')}</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2.5">
          {OPTIONAL_SLOTS[target.kind].map((slot) => (slot === 'INTERIOR' ? interiorTiles() : tile(slot, false)))}
        </div>
      </div>

      <div className="mt-4">
        <Label>{t('inspection.damages')}</Label>
        {damages.length === 0 && pendingDamages.length === 0 && (
          <p className="mt-1 text-xs text-ink-faint">{t('inspection.noDamage')}</p>
        )}
        <ul className="mt-1.5 space-y-2">
          {damages.map((photo) => (
            <li key={photo.id}>
              <button
                onClick={() => setViewingId(photo.id)}
                className="flex w-full items-center gap-3 rounded-xl border border-line bg-raised p-2 text-left transition hover:border-ink-faint"
              >
                <img src={photoUrl(photo.id, 'thumb')} alt="" className="h-14 w-16 shrink-0 rounded-lg object-cover" />
                <span className="min-w-0">
                  <span className="line-clamp-2 text-sm font-medium leading-snug text-warning">{label(photo)}</span>
                  {photo.note && <span className="mt-0.5 line-clamp-2 text-xs text-ink-dim">{photo.note}</span>}
                </span>
              </button>
            </li>
          ))}
          {pendingDamages.map(([key, entry]) => (
            <li key={key}>
              <button
                onClick={() => entry.status === 'failed' && send(key, entry)}
                className="flex w-full items-center gap-3 rounded-xl border border-line bg-raised p-2 text-left"
              >
                <img src={entry.previewUrl} alt="" className="h-14 w-16 shrink-0 rounded-lg object-cover opacity-60" />
                <span className={`text-xs ${entry.status === 'failed' ? 'text-danger' : 'text-ink-dim'}`}>
                  {entry.status === 'failed' ? t('inspection.failed') : t('inspection.uploading')}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <button
          onClick={() => shoot('DAMAGE')}
          disabled={!inspectionId}
          className="mt-2 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-warning/50 text-sm font-semibold text-warning transition hover:border-warning disabled:opacity-50"
        >
          <CameraIcon className="h-5 w-5" />
          {t('inspection.addDamage')}
        </button>
      </div>

      <div className="mt-4">
        <Field
          label={t('inspection.odometer')}
          inputMode="numeric"
          value={odometer}
          onChange={(event) => setOdometer(event.target.value.replace(/\D/g, '').slice(0, 7))}
          onBlur={() => {
            if (!inspectionId) return;
            const value = odometer ? Number(odometer) : null;
            if (value !== (inspection?.odometerKm ?? null)) patch.mutate({ odometerKm: value });
          }}
        />
      </div>

      {scanner}

      {shrinking && (
        <div className="fixed inset-0 z-[55] grid place-items-center bg-black/40">
          <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        </div>
      )}

      {damageDraft && (
        <DamageForm
          photo={damageDraft}
          onCancel={() => { URL.revokeObjectURL(damageDraft.previewUrl); setDamageDraft(null); }}
          onSave={(details) => { queue('DAMAGE', damageDraft, details); setDamageDraft(null); }}
        />
      )}

      {viewingIndex >= 0 && (
        <PhotoViewer
          photos={photos}
          index={viewingIndex}
          onIndex={(index) => setViewingId(photos[index]?.id ?? null)}
          onClose={() => setViewingId(null)}
          actions={inspection?.status === 'DRAFT' ? viewerActions : undefined}
        />
      )}
    </div>
  );
}
